/**
 * Utilitaires de temps pour les champs de PLANNING.
 *
 * ============================================================================
 * Deux régimes coexistent dans le contrat (§4), et les confondre est l'erreur
 * la plus coûteuse de ce lot :
 *
 *   `date` + `heure_debut`  → date et heure LOCALES, séparées, affichées telles
 *                             quelles. Ce module s'en occupe.
 *   `heure_arrivee_reelle`,
 *   `created_at`            → datetime UTC, converti en Europe/Paris à
 *                             l'affichage. Voir `formatters/dates.ts`.
 *
 * **On ne construit jamais un `Date` à partir d'un champ de planning.**
 * `new Date("2026-03-02T08:30")` est interprété dans le fuseau du poste : la
 * même intervention s'affiche à 08:30 chez toi et à 07:30 sur une CI en UTC.
 * Toutes les fonctions ci-dessous manipulent des chaînes "HH:MM" et des
 * entiers de minutes, sans jamais passer par `Date`.
 * ============================================================================
 */

/** "08:30" → 510. Renvoie 0 sur une entrée illisible plutôt que NaN. */
export function minutesDepuisMinuit(heure: string): number {
  const morceaux = heure.split(':')
  const h = Number(morceaux[0])
  const m = Number(morceaux[1])
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0
  return h * 60 + m
}

/** 510 → "08:30". Borné à la journée pour ne jamais produire "25:00". */
export function versHeure(minutes: number): string {
  const borne = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)))
  const h = Math.floor(borne / 60)
  const m = borne % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** "08:30" + 45 → "09:15". Arithmétique de chaînes, sans `Date`. */
export function ajouterMinutes(heure: string, minutes: number): string {
  return versHeure(minutesDepuisMinuit(heure) + minutes)
}

/** Différence en minutes entre deux heures locales de la même journée. */
export function ecartMinutes(debut: string, fin: string): number {
  return minutesDepuisMinuit(fin) - minutesDepuisMinuit(debut)
}

/* ------------------------------------------------------------------------- */
/* Dates de planning : chaînes "AAAA-MM-JJ", manipulées sans fuseau           */
/* ------------------------------------------------------------------------- */

/**
 * Décale une date de planning d'un nombre de jours.
 *
 * `Date.UTC` est utilisé volontairement : à midi UTC, aucun changement d'heure
 * ne peut faire basculer le jour. Le résultat est reformaté en chaîne, donc
 * aucun fuseau ne survit à l'opération.
 */
export function ajouterJours(date: string, jours: number): string {
  const [a, m, j] = date.split('-').map(Number)
  const t = Date.UTC(a ?? 1970, (m ?? 1) - 1, j ?? 1, 12)
  const d = new Date(t + jours * 86_400_000)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate(),
  ).padStart(2, '0')}`
}

/**
 * Lundi de la semaine contenant `date`.
 *
 * **0 = lundi** dans tout le contrat (§5.8). `getUTCDay()` renvoie 0 pour
 * dimanche : la conversion `(jour + 6) % 7` est là pour ça, et l'oublier
 * décale toute la grille d'un jour — un bug qui ne se voit que le dimanche.
 */
export function lundiDeLaSemaine(date: string): string {
  const [a, m, j] = date.split('-').map(Number)
  const d = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, j ?? 1, 12))
  const indexLundiZero = (d.getUTCDay() + 6) % 7
  return ajouterJours(date, -indexLundiZero)
}

/** Date du jour au format planning, dans le fuseau du poste. */
export function aujourdhui(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`
}

/** Les sept dates d'une semaine, du lundi au dimanche. */
export function semaineDepuis(lundi: string): string[] {
  return Array.from({ length: 7 }, (_, i) => ajouterJours(lundi, i))
}

/** 0 = lundi … 6 = dimanche. */
export const JOURS_SEMAINE = [
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche',
] as const

export const JOURS_SEMAINE_COURT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] as const

/* ------------------------------------------------------------------------- */
/* Positionnement dans la grille                                              */
/* ------------------------------------------------------------------------- */

export interface PositionGrille {
  /** Ligne CSS de départ (1 = en-tête, les créneaux commencent à 2). */
  ligneDebut: number
  /** Nombre de lignes occupées, au moins 1. */
  nbLignes: number
  /** Vrai si l'intervention déborde de l'amplitude affichée. */
  deborde: boolean
}

/**
 * Position d'une intervention dans la grille.
 *
 * Une intervention de 15 minutes occupe une ligne entière et se raccourcit par
 * une hauteur interne : subdiviser la grille au quart d'heure doublerait le
 * nombre de lignes pour un gain nul.
 */
export function positionDansGrille(
  heureDebut: string,
  dureeMinutes: number,
  ouverture: string,
  fermeture: string,
  pasMinutes: number,
): PositionGrille {
  const debut = minutesDepuisMinuit(heureDebut)
  const debutGrille = minutesDepuisMinuit(ouverture)
  const finGrille = minutesDepuisMinuit(fermeture)

  const decalage = Math.floor((debut - debutGrille) / pasMinutes)
  const ligneDebut = 2 + Math.max(0, decalage)
  const nbLignes = Math.max(1, Math.round(dureeMinutes / pasMinutes))

  // Déborde par le haut (avant l'ouverture) ou par le bas (après la fermeture).
  const deborde = debut < debutGrille || debut + dureeMinutes > finGrille

  const lignesDisponibles = Math.ceil((finGrille - debutGrille) / pasMinutes) + 2 - ligneDebut
  return {
    ligneDebut,
    nbLignes: Math.max(1, Math.min(nbLignes, Math.max(1, lignesDisponibles))),
    deborde,
  }
}

/** Les libellés de la colonne des heures : 06:00, 06:30, … */
export function creneaux(ouverture: string, fermeture: string, pasMinutes: number): string[] {
  const debut = minutesDepuisMinuit(ouverture)
  const fin = minutesDepuisMinuit(fermeture)
  const out: string[] = []
  for (let m = debut; m < fin; m += pasMinutes) out.push(versHeure(m))
  return out
}
